import { useRef } from 'react';
import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import { AutomatedTestConfigurationApi } from '@elementary/common-ui/types';
import { CircularProgress } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  METADATA_VALUES,
  EXTENDED_METADATA_VALUES_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { UseFormReturn } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSnackbar from 'shared/hooks/useSnackbar';
import useTestsPolling from 'shared/hooks/useTestsPolling';
import * as api from 'shared/services/api';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import { PipelineTaskPerformanceConfigApi } from 'shared/types';
import { space } from '@elementary/common-ui/style/utils';
import useAnomalyDetectionTestConfiguration from '../../hooks/useAnomalyDetectionTestConfiguration';
import { PipelineTaskPerformanceTestConfig, TestOverview } from '../../types';
import AnomalyTestConfigurationFields from './AnomalyTestConfigurationFields';
import { applyThresholdMode } from './utils';

type Props = {
  testOverview: TestOverview;
};

const AnomalyTestConfiguration = ({ testOverview }: Props) => {
  const testConfigRef = useRef<{
    methods: UseFormReturn<
      AutomatedTestConfigurationApi | PipelineTaskPerformanceTestConfig
    >;
    volumeMode?: 'anomaly_detection' | 'percentage_thresholds';
  }>();

  const { openSnackbar } = useSnackbar();
  const { setPollingTestsIds } = useTestsPolling();
  const selectedEnvId = useSelectedEnv();
  const queryClient = useQueryClient();
  const { onPanelConfigSaved } = useSidePanelParams();
  const hasPermission = useEnvPermission();
  const hasEditPermission = hasPermission('EDIT_TEST_CONFIGURATIONS');

  const {
    testConfig,
    anomalyChart,
    updateTestConfig,
    refetchTestConfiguration,
    simulate,
    isFetching,
    isUpdating,
    isSimulating,
    estimatedSensitivityRange,
    savedTrainingPeriod,
  } = useAnomalyDetectionTestConfiguration(testOverview);

  const { data: extendedMetadataValueOptionsData } = useQuery(
    [EXTENDED_METADATA_VALUES_OPTIONS, selectedEnvId],
    () => api.fetchExtendedMetadataValueOptions(selectedEnvId!),
    { enabled: !!selectedEnvId },
  );

  const onSaveSuccess = async () => {
    onPanelConfigSaved();
    await refetchTestConfiguration();
    setPollingTestsIds((prev) => prev.add(testOverview.test.id));
    queryClient.invalidateQueries([METADATA_VALUES, selectedEnvId]);
    queryClient.invalidateQueries([
      EXTENDED_METADATA_VALUES_OPTIONS,
      selectedEnvId,
    ]);
    openSnackbar({
      message: 'Test configuration saved, it will be applied shortly.',
      variant: 'success',
    });
  };

  const onSaveError = () => {
    openSnackbar({
      message: 'Failed to save test configuration.',
      variant: 'error',
    });
  };

  const handleSaveChanges = () => {
    const values = testConfigRef.current?.methods.getValues();
    const volumeMode = testConfigRef.current?.volumeMode;

    if (values?.type === 'pipeline_task_performance') {
      const pv = values;
      const payload: PipelineTaskPerformanceConfigApi = {
        type: 'pipeline_task_performance',
        severity: pv.severity,
        owners: pv.owners,
        tags: pv.tags,
        config: {
          mode: pv.mode,
          sensitivity: pv.sensitivity ?? null,
          training_period: pv.training_period ?? null,
          anomaly_direction: pv.anomaly_direction ?? null,
          fixed_threshold: pv.fixed_threshold ?? null,
        },
      };
      updateTestConfig(payload, {
        onSuccess: onSaveSuccess,
        onError: onSaveError,
      });
      return;
    }

    if (!values) return;
    const anomalyValues = values;
    if (anomalyValues.type === 'anomaly_detection' && volumeMode) {
      anomalyValues.anomaly_config = applyThresholdMode(
        anomalyValues.anomaly_config,
        volumeMode,
      );
    }
    if (anomalyValues.anomaly_config.fixed_threshold != null) {
      anomalyValues.anomaly_config.failure_threshold_grace = null;
    }
    updateTestConfig(anomalyValues, {
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });
  };

  return (
    <FlexColumn sx={{ gap: 3 }}>
      {isFetching && (
        <FlexCenter sx={{ height: '100%', padding: space(0, 'xxl') }}>
          <CircularProgress size={32} />
        </FlexCenter>
      )}

      {!isFetching && testConfig && (
        <AnomalyTestConfigurationFields
          ref={testConfigRef}
          anomalyConfig={
            testConfig as
              | AutomatedTestConfigurationApi
              | PipelineTaskPerformanceTestConfig
          }
          anomalyChart={anomalyChart}
          onSimulate={simulate}
          isLoading={isUpdating}
          isSimulating={isSimulating}
          tagsOptions={extendedMetadataValueOptionsData?.tags}
          ownersOptions={extendedMetadataValueOptionsData?.owners}
          estimatedSensitivityRange={estimatedSensitivityRange}
          onSave={handleSaveChanges}
          readOnly={!hasEditPermission}
          savedTrainingPeriod={savedTrainingPeriod}
        />
      )}
    </FlexColumn>
  );
};

export default AnomalyTestConfiguration;
